import {
  formatClockOffset,
  getClockOffset,
  parseClockInputToOffsetMs,
  setClockOffset,
  timeNow,
} from '../utils/clock';
import type { Operator } from '@/data/operator';
import { $connectedServer } from '@/stores/connectedServer';
import { openDialog } from '@/stores/dialog';
import { alert, confirm } from '@/utils/alertUtils';
import { formatTime } from '@/utils/dataUtils';
import { useStore } from '@nanostores/react';
import { useEffect, useState } from 'react';

interface NewLogbookWizardProps {
  onClose: () => void;
  onSave: (config: LogbookConfig) => void;
}

export interface LogbookConfig {
  assignment: string;
  date: string;
  myCallsign: string;
  defaultFrequency: string;
  operators: Operator[];
  codeWords: Record<string, string>;
  clockOffset?: number;
}

const stepOrder = [
  'assignment',
  'callsign',
  'frequency',
  'operators',
  'codewords',
  'clock',
  'summary',
] as const;
type WizardStep = (typeof stepOrder)[number];

export function NewLogbookWizard({ onClose, onSave }: NewLogbookWizardProps) {
  const [step, setStep] = useState<WizardStep>('assignment');
  const connectedServer = useStore($connectedServer);
  const [assignment, setAssignment] = useState('Default Assignment');
  const [date, setDate] = useState(
    () => new Date().toISOString().split('T')[0]!,
  );
  const [myCallsign, setMyCallsign] = useState('0');
  const [defaultFrequency, setDefaultFrequency] = useState('14.070');
  const [operators, setOperators] = useState<Operator[]>([]);
  const [codeWords, setCodeWords] = useState<Record<string, string>>({});

  // Operator form fields
  const [newOperatorName, setNewOperatorName] = useState('');

  // Code words form fields
  const [newCodeWordName, setNewCodeWordName] = useState('');
  const [newCodeWordContent, setNewCodeWordContent] = useState('');

  // Clock adjustment fields
  const [correctTime, setCorrectTime] = useState('');
  const [currentDisplay, setCurrentDisplay] = useState('');

  useEffect(() => {
    const update = () => setCurrentDisplay(formatTime(timeNow()));
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (connectedServer) {
      onClose();
    }
  }, [connectedServer, onClose]);

  const handleNext = () => {
    const currentIndex = stepOrder.indexOf(step);
    if (currentIndex < stepOrder.length - 1) {
      setStep(stepOrder[currentIndex + 1]!);
    }
  };

  const handleBack = () => {
    const currentIndex = stepOrder.indexOf(step);
    if (currentIndex > 0) {
      setStep(stepOrder[currentIndex - 1]!);
    }
  };

  const handleFinish = () => {
    onSave({
      assignment,
      date,
      myCallsign,
      defaultFrequency,
      operators,
      codeWords,
    });
    onClose();
  };

  const handleSkip = () => {
    onClose();
  };

  const addOperator = () => {
    if (!newOperatorName.trim()) return;
    const newOp: Operator = {
      id: `op-${Date.now()}`,
      name: newOperatorName.trim(),
    };
    setOperators([...operators, newOp]);
    setNewOperatorName('');
  };

  const removeOperator = (id: string) => {
    setOperators(operators.filter(op => op.id !== id));
  };

  const addCodeWord = () => {
    if (!newCodeWordName.trim()) return;
    const normalizedName = newCodeWordName.trim().toUpperCase();
    setCodeWords({
      ...codeWords,
      [normalizedName]: newCodeWordContent,
    });
    setNewCodeWordName('');
    setNewCodeWordContent('');
  };

  const removeCodeWord = (name: string) => {
    const newCodeWords = { ...codeWords };
    delete newCodeWords[name];
    setCodeWords(newCodeWords);
  };

  const handleApplyClock = () => {
    const newOffset = parseClockInputToOffsetMs(correctTime);
    if (newOffset === null) {
      alert({
        title: 'Error',
        message: 'Invalid time value',
      });
      return;
    }
    setClockOffset(newOffset);
    setCorrectTime('');
  };

  const handleResetClock = () => {
    setClockOffset(0);
    setCorrectTime('');
  };

  const offsetMs = getClockOffset();
  const offsetLabel = formatClockOffset(offsetMs);

  const renderStepContent = () => {
    switch (step) {
      case 'assignment':
        return (
          <div className="space-y-4">
            <div>
              <label
                htmlFor="assignment"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                Assignment Name
              </label>
              <input
                id="assignment"
                type="text"
                value={assignment}
                onChange={e => setAssignment(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                placeholder="e.g., Training Exercise Alpha"
                autoFocus
              />
            </div>
            <div>
              <label
                htmlFor="date"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                Date
              </label>
              <input
                id="date"
                type="date"
                value={date}
                onChange={e => setDate(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>
            <div className="rounded-md border border-blue-200 bg-blue-50 p-4">
              <p className="text-sm text-blue-900">
                If you want to connect to a remote server before finishing
                setup, open the connection dialog here.
              </p>
              <button
                type="button"
                onClick={() => openDialog('connectServer')}
                className="mt-3 rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              >
                Connect to Server
              </button>
            </div>
          </div>
        );

      case 'callsign':
        return (
          <div>
            <label
              htmlFor="callsign"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              Your Call Sign
            </label>
            <input
              id="callsign"
              type="text"
              value={myCallsign}
              onChange={e => setMyCallsign(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              placeholder="e.g., 0, 1, or Alpha"
              autoFocus
            />
            <p className="mt-2 text-sm text-gray-500">
              This is your station's call sign. It will be used as the default
              "from" call sign in messages.
            </p>
          </div>
        );

      case 'frequency':
        return (
          <div>
            <label
              htmlFor="frequency"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              Default Frequency
            </label>
            <input
              id="frequency"
              type="text"
              value={defaultFrequency}
              onChange={e => setDefaultFrequency(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              placeholder="e.g., 14.070"
              autoFocus
            />
            <p className="mt-2 text-sm text-gray-500">
              This frequency will be used by default when creating new log
              entries.
            </p>
          </div>
        );

      case 'operators':
        return (
          <div className="space-y-4">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Add Operators
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newOperatorName}
                  onChange={e => setNewOperatorName(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addOperator();
                    }
                  }}
                  className="flex-1 rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="Operator name"
                  autoFocus
                />
                <button
                  onClick={addOperator}
                  className="rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                >
                  Add
                </button>
              </div>
            </div>
            {operators.length > 0 && (
              <div>
                <p className="mb-2 text-sm font-medium text-gray-700">
                  Current Operators:
                </p>
                <div className="space-y-2">
                  {operators.map(op => (
                    <div
                      key={op.id}
                      className="flex items-center justify-between rounded border border-gray-200 bg-gray-50 p-2"
                    >
                      <span>{op.name}</span>
                      <button
                        onClick={() => removeOperator(op.id)}
                        className="text-sm text-red-600 hover:text-red-800"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <p className="text-sm text-gray-500">
              {operators.length === 0
                ? 'Optional: Add operators who will be using this logbook. You can skip this step and add them later.'
                : 'You can add more operators or continue to the next step.'}
            </p>
          </div>
        );

      case 'codewords':
        return (
          <div className="space-y-4">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Add Code Words
              </label>
              <div className="space-y-2">
                <input
                  type="text"
                  value={newCodeWordName}
                  onChange={e => setNewCodeWordName(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="Code word (e.g., ENDEX)"
                  autoFocus
                />
                <textarea
                  value={newCodeWordContent}
                  onChange={e => setNewCodeWordContent(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                      e.preventDefault();
                      addCodeWord();
                    }
                  }}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="Meaning"
                  rows={2}
                />
                <button
                  onClick={addCodeWord}
                  className="w-full rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                >
                  Add Code Word
                </button>
              </div>
            </div>
            {Object.keys(codeWords).length > 0 && (
              <div>
                <p className="mb-2 text-sm font-medium text-gray-700">
                  Current Code Words:
                </p>
                <div className="space-y-2">
                  {Object.entries(codeWords).map(([name, content]) => (
                    <div
                      key={name}
                      className="rounded border border-gray-200 bg-gray-50 p-3"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900">{name}</p>
                          <p className="text-sm text-gray-600">{content}</p>
                        </div>
                        <button
                          onClick={() => removeCodeWord(name)}
                          className="ml-2 text-sm text-red-600 hover:text-red-800"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <p className="text-sm text-gray-500">
              Optional: Add code words for quick reference. You can skip this
              step and add them later.
            </p>
          </div>
        );

      case 'clock':
        return (
          <div className="space-y-4">
            <div className="space-y-1 rounded bg-gray-100 p-3">
              <p className="text-sm text-gray-600">
                Current (adjusted) time:{' '}
                <span className="font-mono font-bold text-gray-900">
                  {currentDisplay}
                </span>
              </p>
              <p className="text-sm text-gray-500">
                Offset: <span className="font-mono">{offsetLabel}</span>
              </p>
            </div>

            <div>
              <label
                htmlFor="correct-time"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                Set Correct Time (HH:MM)
              </label>
              <input
                id="correct-time"
                type="text"
                value={correctTime}
                onChange={e => setCorrectTime(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleApplyClock();
                  }
                }}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                placeholder="e.g., 14:30"
                autoFocus
              />
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleApplyClock}
                className="flex-1 rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              >
                Apply Adjustment
              </button>
              <button
                onClick={handleResetClock}
                className="rounded-md bg-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-400 focus:ring-2 focus:ring-gray-500 focus:outline-none"
              >
                Reset
              </button>
            </div>

            <p className="text-sm text-gray-500">
              Optional: If your system clock is incorrect, you can adjust it
              here. This will only affect timestamps in this logbook.
            </p>
          </div>
        );

      case 'summary':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Review Your Configuration
            </h3>
            <div className="space-y-3 text-sm">
              <div className="rounded bg-gray-50 p-3">
                <p className="font-medium text-gray-700">Assignment:</p>
                <p className="text-gray-900">{assignment}</p>
              </div>
              <div className="rounded bg-gray-50 p-3">
                <p className="font-medium text-gray-700">Date:</p>
                <p className="text-gray-900">{date}</p>
              </div>
              <div className="rounded bg-gray-50 p-3">
                <p className="font-medium text-gray-700">Call Sign:</p>
                <p className="text-gray-900">{myCallsign}</p>
              </div>
              <div className="rounded bg-gray-50 p-3">
                <p className="font-medium text-gray-700">Default Frequency:</p>
                <p className="text-gray-900">{defaultFrequency}</p>
              </div>
              {operators.length > 0 && (
                <div className="rounded bg-gray-50 p-3">
                  <p className="font-medium text-gray-700">
                    Operators ({operators.length}):
                  </p>
                  <ul className="list-inside list-disc text-gray-900">
                    {operators.map(op => (
                      <li key={op.id}>{op.name}</li>
                    ))}
                  </ul>
                </div>
              )}
              {Object.keys(codeWords).length > 0 && (
                <div className="rounded bg-gray-50 p-3">
                  <p className="font-medium text-gray-700">
                    Code Words ({Object.keys(codeWords).length}):
                  </p>
                  <ul className="list-inside list-disc text-gray-900">
                    {Object.keys(codeWords).map(name => (
                      <li key={name}>{name}</li>
                    ))}
                  </ul>
                </div>
              )}
              {offsetMs !== 0 && (
                <div className="rounded bg-gray-50 p-3">
                  <p className="font-medium text-gray-700">Clock Adjustment:</p>
                  <p className="text-gray-900">{offsetLabel}</p>
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const stepTitles: Record<WizardStep, string> = {
    assignment: 'Assignment Details',
    callsign: 'Call Sign',
    frequency: 'Default Frequency',
    operators: 'Operators',
    codewords: 'Code Words',
    clock: 'Clock Adjustment',
    summary: 'Summary',
  };

  const stepNumbers: Record<WizardStep, number> = {
    assignment: 1,
    callsign: 2,
    frequency: 3,
    operators: 4,
    codewords: 5,
    clock: 6,
    summary: 7,
  };

  return (
    <div
      className="stop-scrolling fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={async e => {
        if (e.target === e.currentTarget) {
          if (
            await confirm({
              title: 'Cancel Wizard',
              message:
                'Are you sure you want to cancel? Your progress will be lost.',
            })
          ) {
            onClose();
          }
        }
      }}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-lg bg-white shadow-xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-semibold text-gray-900">
                New Logbook Setup
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                Step {stepNumbers[step]} of 7: {stepTitles[step]}
              </p>
            </div>
            <button
              onClick={handleSkip}
              className="text-sm text-gray-500 underline hover:text-gray-700"
            >
              Skip Wizard
            </button>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="px-6 pt-4">
          <div className="h-2 w-full rounded-full bg-gray-200">
            <div
              className="h-2 rounded-full bg-blue-600 transition-all duration-300"
              style={{ width: `${(stepNumbers[step] / 7) * 100}%` }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {renderStepContent()}
        </div>

        {/* Footer */}
        <div className="flex justify-between border-t border-gray-200 px-6 py-4">
          <button
            onClick={handleBack}
            disabled={step === 'assignment'}
            className="rounded-md bg-gray-100 px-4 py-2 text-gray-700 not-disabled:hover:bg-gray-200 focus:ring-2 focus:ring-gray-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
          >
            Back
          </button>
          <div className="flex gap-2">
            {step === 'summary' ? (
              <button
                onClick={handleFinish}
                className="rounded-md bg-green-600 px-6 py-2 text-white hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:outline-none"
              >
                Finish
              </button>
            ) : (
              <button
                onClick={handleNext}
                className="rounded-md bg-blue-600 px-6 py-2 text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
